import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ContentPageHeader, MetricGrid, SummaryMetricCard, TableLink } from '@/components/global';
import { CollapsibleSection } from '@/components/global/CollapsibleSection';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { DollarSign, TrendingUp, Percent, ArrowLeft, Download } from 'lucide-react';
import { downloadCsv } from '@/lib/csvExport';

interface SpokePnL {
  spokeId: string;
  spokeName: string;
  totalRevenue: number;
  totalProductCost: number;
  totalGrossProfit: number;
  totalVenueCommission: number;
  totalRefillCost: number;
  spokeOperatingCost: number;
  netProfit: number;
  marginPercent: number;
}

interface HubPnL {
  hubId: string;
  hubName: string;
  spokeCount: number;
  totalMachineCount: number;
  totalRevenue: number;
  totalRefunds: number;
  totalNetRevenue: number;
  totalProductCost: number;
  totalGrossProfit: number;
  totalVenueCommission: number;
  totalRefillCost: number;
  totalSpokeOperatingCost: number;
  totalDeliveryCost: number;
  hubOperatingCost: number;
  netProfit: number;
  marginPercent: number;
  spokes: SpokePnL[];
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export default function HubPnLPage() {
  const { id } = useParams<{ id: string }>();
  const { dateRange, setDateRange } = useDateRange('MTD');
  const [pnl, setPnl] = useState<HubPnL | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      });

      const res = await apiFetch(`/api/v1/finance/pnl/hub/${id}?${params}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Hub not found');
        } else {
          setError('Failed to load P&L data');
        }
        return;
      }

      const data = await res.json();
      setPnl(data);
    } catch (error) {
      console.error('Error loading hub P&L:', error);
      setError('Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  }, [id, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function exportCsv() {
    if (!pnl || pnl.spokes.length === 0) return;
    downloadCsv(
      `hub-pnl-${pnl.hubName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Spoke',
        'Revenue (GBP)',
        'COGS (GBP)',
        'Gross profit (GBP)',
        'Commission (GBP)',
        'Operating costs (GBP)',
        'Net profit (GBP)',
        'Margin %',
      ],
      pnl.spokes.map((spoke) => [
        spoke.spokeName,
        spoke.totalRevenue.toFixed(2),
        spoke.totalProductCost.toFixed(2),
        spoke.totalGrossProfit.toFixed(2),
        spoke.totalVenueCommission.toFixed(2),
        spoke.spokeOperatingCost.toFixed(2),
        spoke.netProfit.toFixed(2),
        spoke.marginPercent.toFixed(1),
      ])
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-red-600">{error}</h2>
            <Link to="/finance">
              <Button variant="outline" className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Finance
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <ContentPageHeader
          back={{ to: '/finance' }}
          title={`P&L: ${loading ? 'Loading...' : pnl?.hubName || 'Hub'}`}
          actions={
            <>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full sm:w-auto"
              />
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={loading || !pnl || pnl.spokes.length === 0}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </>
          }
        />

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Revenue"
            value={pnl ? formatCurrency(pnl.totalNetRevenue) : '-'}
            icon={DollarSign}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Gross Profit"
            value={pnl ? formatCurrency(pnl.totalGrossProfit) : '-'}
            icon={TrendingUp}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Net Profit"
            value={pnl ? formatCurrency(pnl.netProfit) : '-'}
            icon={TrendingUp}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Margin"
            value={pnl ? formatPercent(pnl.marginPercent) : '-'}
            icon={Percent}
            isLoading={loading}
          />
        </MetricGrid>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <CollapsibleSection title="Revenue" defaultOpen>
            <Card>
              <CardContent className="pt-4">
                {loading ? (
                  <div className="text-center py-4">Loading...</div>
                ) : pnl ? (
                  <div className="space-y-2">
                    <div className="flex justify-between py-2">
                      <span>Sales Revenue</span>
                      <span>{formatCurrency(pnl.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between py-2 text-red-600">
                      <span>Refunds</span>
                      <span>-{formatCurrency(pnl.totalRefunds)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t font-bold">
                      <span>Net Revenue</span>
                      <span>{formatCurrency(pnl.totalNetRevenue)}</span>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </CollapsibleSection>

          <CollapsibleSection title="Cost of Goods Sold" defaultOpen>
            <Card>
              <CardContent className="pt-4">
                {loading ? (
                  <div className="text-center py-4">Loading...</div>
                ) : pnl ? (
                  <div className="space-y-2">
                    <div className="flex justify-between py-2">
                      <span>Product Purchase Cost</span>
                      <span>{formatCurrency(pnl.totalProductCost)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t font-bold">
                      <span>Total COGS</span>
                      <span>{formatCurrency(pnl.totalProductCost)}</span>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </CollapsibleSection>
        </div>

        <CollapsibleSection title="Operating Expenses" defaultOpen className="mb-6">
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : pnl ? (
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span>Venue Commission</span>
                    <span>{formatCurrency(pnl.totalVenueCommission)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Gig Worker Refills</span>
                    <span>{formatCurrency(pnl.totalRefillCost)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Spoke Operating Costs</span>
                    <span>{formatCurrency(pnl.totalSpokeOperatingCost)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Hub Operating Costs</span>
                    <span>{formatCurrency(pnl.hubOperatingCost)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>Delivery Costs</span>
                    <span>{formatCurrency(pnl.totalDeliveryCost)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t font-bold">
                    <span>Total Operating Expenses</span>
                    <span>
                      {formatCurrency(
                        pnl.totalVenueCommission +
                          pnl.totalRefillCost +
                          pnl.totalSpokeOperatingCost +
                          pnl.hubOperatingCost +
                          pnl.totalDeliveryCost
                      )}
                    </span>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </CollapsibleSection>

        <CollapsibleSection title="Net Profit" defaultOpen className="mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : pnl ? (
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-700">{formatCurrency(pnl.netProfit)}</div>
                  <div className="text-lg text-green-600">{formatPercent(pnl.marginPercent)} Margin</div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </CollapsibleSection>

        <CollapsibleSection title="Spoke Breakdown" defaultOpen>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Spoke</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">COGS</TableHead>
                  <TableHead className="text-right">Gross Profit</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Op. Costs</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : pnl?.spokes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No spokes found
                    </TableCell>
                  </TableRow>
                ) : (
                  pnl?.spokes.map((spoke) => (
                    <TableRow key={spoke.spokeId}>
                      <TableCell>
                        <TableLink to={`/finance/spokes/${spoke.spokeId}/pnl`}>
                          {spoke.spokeName}
                        </TableLink>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(spoke.totalRevenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(spoke.totalProductCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(spoke.totalGrossProfit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(spoke.totalVenueCommission)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(spoke.spokeOperatingCost)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(spoke.netProfit)}</TableCell>
                      <TableCell className="text-right">{formatPercent(spoke.marginPercent)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </CollapsibleSection>
      </div>
    </div>
  );
}
